// The lesson tree of a saved course: sortable lesson cards (dnd-kit), each
// expandable to its videos and knowledge check, plus add / edit in a modal.
// Lesson changes save straight away; the page reloads lessons afterwards.

import { useCallback, useEffect, useState, type FormEvent } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { IconChevronRight, IconGripVertical, IconPencil, IconPlus, IconTrash } from "@tabler/icons-react";
import { formatMinutes, type Lesson } from "../../types/course";
import { errorText } from "../../services/courses/auth";
import { createLesson, deleteLesson, reorderLessons, updateLesson } from "../../services/courses/lesson.service";
import { Modal } from "../Modal";
import { VideoManager } from "./VideoManager";

function SortableLesson({
  courseId,
  lesson,
  index,
  open,
  disabled,
  onToggle,
  onEdit,
  onDelete,
  onChanged,
}: {
  courseId: string;
  lesson: Lesson;
  index: number;
  open: boolean;
  disabled: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onChanged: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: lesson.id!,
    disabled,
  });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

  return (
    <div ref={setNodeRef} style={style} className={`co-lesson${open ? " open" : ""}`}>
      <div className="co-lesson-head" onClick={onToggle}>
        <button type="button" className="co-grip" title="Drag to reorder" aria-label={`Reorder ${lesson.title}`}
          disabled={disabled} onClick={(e) => e.stopPropagation()} {...attributes} {...listeners}>
          <IconGripVertical size={14} />
        </button>
        <span className="co-pos">{index + 1}</span>
        <span className="co-caret"><IconChevronRight size={12} /></span>
        <div className="co-lesson-title">
          <h3>{lesson.title}</h3>
          {lesson.description && <p>{lesson.description}</p>}
        </div>
        <div className="co-lesson-meta">
          <span>{formatMinutes(lesson.totalDuration)}</span>
          {lesson.knowledgeCheck && (
            <span className="kc" title={`${lesson.knowledgeCheck.questions.length} questions`}>✓ check</span>
          )}
        </div>
        <div className="co-actions" onClick={(e) => e.stopPropagation()}>
          <button type="button" className="co-btn-icon" onClick={onEdit} disabled={disabled} title="Edit lesson" aria-label="Edit lesson">
            <IconPencil size={14} />
          </button>
          <button type="button" className="co-btn-icon danger" onClick={onDelete} disabled={disabled} title="Delete lesson" aria-label="Delete lesson">
            <IconTrash size={14} />
          </button>
        </div>
      </div>
      {open && <VideoManager courseId={courseId} lesson={lesson} onChanged={onChanged} disabled={disabled} />}
    </div>
  );
}

export function LessonManager({
  courseId,
  lessons,
  onChanged,
  disabled,
}: {
  courseId: string;
  lessons: Lesson[];
  // Called after any write so the page can reload lessons and course totals.
  onChanged: () => void;
  disabled?: boolean;
}) {
  // Local copy so a drag reorders instantly; the prop wins when it reloads.
  const [ordered, setOrdered] = useState<Lesson[]>(lessons);
  useEffect(() => setOrdered(lessons), [lessons]);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Lesson | null>(null);
  const [draft, setDraft] = useState({ title: "", description: "" });
  const [draftError, setDraftError] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function toggle(id: string) {
    setExpanded((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function openModal(lesson: Lesson | null) {
    setEditing(lesson);
    setDraft({ title: lesson?.title ?? "", description: lesson?.description ?? "" });
    setDraftError(null);
    setModalOpen(true);
  }

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditing(null);
    setDraftError(null);
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const title = draft.title.trim();
    const description = draft.description.trim();
    if (!title) return setDraftError("Please enter a lesson title.");
    const duplicate = ordered.some((l) => l.id !== editing?.id && l.title.toLowerCase() === title.toLowerCase());
    if (duplicate) return setDraftError("A lesson with this title already exists. Please choose a different title.");

    setBusy(true);
    setDraftError(null);
    try {
      if (editing) {
        await updateLesson(courseId, editing.id!, { title, description });
      } else {
        // Deletes do not renumber, so the list length could collide with an existing order.
        const order = ordered.reduce((max, l) => Math.max(max, l.order + 1), 0);
        const id = await createLesson(courseId, { title, description, order });
        setExpanded((cur) => new Set(cur).add(id));
      }
      closeModal();
      onChanged();
    } catch (err) {
      setDraftError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function remove(lesson: Lesson) {
    if (!confirm(`Delete “${lesson.title}”? Its videos and knowledge check go with it.`)) return;
    setBusy(true);
    setError(null);
    try {
      await deleteLesson(courseId, lesson.id!);
      onChanged();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  async function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ordered.findIndex((l) => l.id === active.id);
    const newIndex = ordered.findIndex((l) => l.id === over.id);
    const reordered = arrayMove(ordered, oldIndex, newIndex);
    setOrdered(reordered); // optimistic; the order field follows
    try {
      await reorderLessons(courseId, reordered.map((l) => l.id!));
    } catch (e) {
      setError(`Reorder failed: ${errorText(e)}`);
    }
    // Reload either way: the page's copy (side panel) follows the new order,
    // or the list snaps back to the stored one after a failure.
    onChanged();
  }

  const locked = !!disabled || busy;

  return (
    <>
      {error && <div className="error-text">{error}</div>}
      {ordered.length === 0 ? (
        <div className="callout">No lessons yet. Click “Add lesson” to create the first one.</div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={ordered.map((l) => l.id!)} strategy={verticalListSortingStrategy}>
            <div className="co-lesson-list">
              {ordered.map((l, i) => (
                <SortableLesson
                  key={l.id}
                  courseId={courseId}
                  lesson={l}
                  index={i}
                  open={expanded.has(l.id!)}
                  disabled={locked}
                  onToggle={() => toggle(l.id!)}
                  onEdit={() => openModal(l)}
                  onDelete={() => remove(l)}
                  onChanged={onChanged}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
      <div className="co-lesson-foot">
        <button type="button" className="btn" onClick={() => openModal(null)} disabled={locked}>
          <IconPlus size={14} /> Add lesson
        </button>
        <span className="co-hint">
          Drag the handle to reorder lessons and videos. Lesson and video changes save straight away; the course
          form needs Save.
        </span>
      </div>

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={editing ? "Edit lesson" : "Add lesson"}
        footer={
          <>
            <button type="submit" form="co-lesson-form" className="btn btn-primary" disabled={busy}>
              {busy ? "Saving…" : editing ? "Update lesson" : "Add lesson"}
            </button>
            <button type="button" className="btn" onClick={closeModal} disabled={busy}>
              Cancel
            </button>
            {draftError && <span className="error-text">{draftError}</span>}
          </>
        }
      >
        <form id="co-lesson-form" onSubmit={submit}>
          <label htmlFor="co-lesson-title">Lesson title</label>
          <input id="co-lesson-title" type="text" value={draft.title} placeholder="e.g. Mental health is health"
            onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
          <label htmlFor="co-lesson-desc">
            Description <span className="muted">(optional)</span>
          </label>
          <textarea id="co-lesson-desc" rows={3} value={draft.description} placeholder="Brief description of this lesson"
            onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
          {!editing && <div className="co-hint">The lesson is added at the end of the course. Drag it to move it.</div>}
        </form>
      </Modal>
    </>
  );
}
